// Apolo IDE — PlanProposalCard (sprint Plan Mode v2, E3).
//
// O cartão que o agente mostra quando chama a ferramenta `present_plan`: um
// PLANO PROPOSTO renderizado com paridade Claude Code / Cursor — resumo + lista
// de passos numerada (to-do) + perguntas de esclarecimento CLICÁVEIS com uma
// opção "Outro" de texto livre, e os botões Aprovar / Rejeitar.
//
// É 100% apresentacional: recebe o plano por props e devolve as respostas via
// callbacks. Nenhum estado de sessão, nenhuma chamada de rede — o agente de
// fiação liga `onApprove`/`onReject` ao loop.
//
// Linguagem visual: o chat do Code é escopado em `.rx-shell`, que flipa
// `--apolo-accent` pro LARANJA do Cursor. Seguimos a doutrina Cursor (calmo,
// quente, técnico, denso): UM acento usado com parcimônia (chip selecionado +
// botão primário Aprovar + marcador do passo), bordas finíssimas
// (`apolo-borderSubtle`), cantos `apolo-md/lg`, mono nos metadados/números,
// peso 400 dominante. Tokens `apolo-*` são theme-aware → paridade dark/light
// automática. Acessibilidade: `<fieldset>`/`<legend>` por pergunta, radios/
// checkboxes REAIS estilizados como chips (input sr-only, label clicável com
// ring de foco), AA, `prefers-reduced-motion` respeitado (via classes rx-* da
// redesign.css + transições curtas que o utilitário global zera).
//
// Contrato de props (congelado):
//   summary:   string
//   steps:     [{ n, action, tool?, success? }]
//   questions: [{ id, question, options:[string], allowOther?, multi? }]
//   onApprove: (answers) => void   // answers = [{ questionId, value }]
//   onReject:  (feedback) => void  // feedback = string (uma linha)

import { useEffect, useMemo, useState } from 'react';
import { Check, ListChecks, MessageSquare, Pencil, Send, X } from 'lucide-react';

const OTHER_VALUE = '__other__';

// Cada pergunta vira `{ choice, otherText }` no estado:
//   choice (single) = string | OTHER_VALUE | ''
//   choice (multi)  = Array<string | OTHER_VALUE>
// `otherText` é o texto livre digitado quando "Outro" está selecionado.
function initialAnswerState(questions) {
  const out = {};
  for (const q of questions) {
    out[q.id] = { choice: q.multi ? [] : '', otherText: '' };
  }
  return out;
}

// Resolve a resposta final de UMA pergunta para o payload de onApprove.
// Devolve `null` quando a pergunta ficou sem resposta (omitimos — não bloqueia).
function resolveAnswer(q, st) {
  if (!st) return null;
  const otherText = (st.otherText || '').trim();

  if (q.multi) {
    const picked = Array.isArray(st.choice) ? st.choice : [];
    const values = picked
      .map((c) => (c === OTHER_VALUE ? otherText : c))
      .filter((v) => v !== '' && v != null);
    return values.length ? { questionId: q.id, value: values } : null;
  }

  if (st.choice === OTHER_VALUE) {
    return otherText ? { questionId: q.id, value: otherText } : null;
  }
  if (st.choice === '' || st.choice == null) return null;
  return { questionId: q.id, value: st.choice };
}

// Normaliza/saneia os passos — props malformadas não devem derrubar o render.
function normalizeSteps(steps) {
  if (!Array.isArray(steps)) return [];
  return steps
    .filter((s) => s && typeof s === 'object')
    .map((s, i) => ({
      n: Number.isFinite(s.n) ? s.n : i + 1,
      action: typeof s.action === 'string' ? s.action : '',
      tool: typeof s.tool === 'string' && s.tool ? s.tool : null,
      success: typeof s.success === 'string' && s.success ? s.success : null,
    }))
    .filter((s) => s.action);
}

function normalizeQuestions(questions) {
  if (!Array.isArray(questions)) return [];
  return questions
    .filter((q) => q && typeof q === 'object' && q.id && typeof q.question === 'string')
    .map((q) => ({
      id: String(q.id),
      question: q.question,
      options: Array.isArray(q.options) ? q.options.filter((o) => typeof o === 'string') : [],
      // "Outro" é SEMPRE oferecido (headline da feature). allowOther:false desliga.
      allowOther: q.allowOther !== false,
      multi: Boolean(q.multi),
    }));
}

export default function PlanProposalCard({
  summary,
  steps,
  questions,
  onApprove,
  onReject,
}) {
  const safeSteps = useMemo(() => normalizeSteps(steps), [steps]);
  const safeQuestions = useMemo(() => normalizeQuestions(questions), [questions]);

  const [answers, setAnswers] = useState(() => initialAnswerState(safeQuestions));
  const [rejecting, setRejecting] = useState(false);
  const [feedback, setFeedback] = useState('');

  // Edge: plano sem resumo E sem passos = nada renderizável → não quebra, some.
  const hasSummary = typeof summary === 'string' && summary.trim().length > 0;
  const isEmpty = !hasSummary && safeSteps.length === 0;

  function pickSingle(qid, value) {
    setAnswers((prev) => ({ ...prev, [qid]: { ...prev[qid], choice: value } }));
  }

  function toggleMulti(qid, value) {
    setAnswers((prev) => {
      const cur = Array.isArray(prev[qid]?.choice) ? prev[qid].choice : [];
      const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
      return { ...prev, [qid]: { ...prev[qid], choice: next } };
    });
  }

  function setOtherText(qid, text) {
    setAnswers((prev) => ({ ...prev, [qid]: { ...prev[qid], otherText: text } }));
  }

  function isSelected(qid, value, multi) {
    const choice = answers[qid]?.choice;
    return multi ? Array.isArray(choice) && choice.includes(value) : choice === value;
  }

  function handleApprove() {
    const payload = safeQuestions
      .map((q) => resolveAnswer(q, answers[q.id]))
      .filter(Boolean);
    onApprove?.(payload);
  }

  function handleRejectClick() {
    if (!rejecting) {
      setRejecting(true);
      return;
    }
    onReject?.(feedback.trim());
  }

  // Acessibilidade: aprovar o plano por TECLADO (⌘/Ctrl+Enter), paridade com o
  // botão Aprovar. Sem isto o gate do Plan Mode só saía por clique — inacessível
  // por teclado/leitor de tela e por automação, e num clique bloqueado o run ficava
  // preso re-planejando. Guarda de activeElement: se o foco está num campo de texto
  // (composer/feedback), o ⌘Enter continua sendo "enviar/ajustar" — só aprova quando
  // o foco NÃO está digitando.
  useEffect(() => {
    function onKey(e) {
      if (!((e.metaKey || e.ctrlKey) && e.key === 'Enter') || rejecting) return;
      const el = typeof document !== 'undefined' ? document.activeElement : null;
      const typing = !!el && (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT' || (el instanceof HTMLElement && el.isContentEditable));
      if (typing) return;
      e.preventDefault();
      handleApprove();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fecha sobre answers/questions vivos
  }, [rejecting, answers, safeQuestions]);

  if (isEmpty) return null;

  return (
    <section
      className="workbench-plan-card rounded-apolo-lg border border-apolo-borderSubtle bg-apolo-surface1/70 text-[13px] text-apolo-textPrimary shadow-sm"
      data-testid="plan-proposal-card"
      aria-label="Plano proposto"
    >
      {/* ── Header: eyebrow mono + ícone de acento sutil ── */}
      <header className="flex items-center gap-2 border-b border-apolo-borderSubtle px-4 py-2.5">
        <ListChecks size={15} className="shrink-0 text-apolo-accent" aria-hidden="true" />
        <span className="font-mono text-[11px] uppercase tracking-wider text-apolo-textTertiary">
          Plano proposto
        </span>
        {safeSteps.length > 0 && (
          <span className="ml-auto font-mono text-[11px] tabular-nums text-apolo-textTertiary">
            {safeSteps.length} {safeSteps.length === 1 ? 'passo' : 'passos'}
          </span>
        )}
      </header>

      <div className="flex flex-col gap-4 px-4 py-3.5">
        {hasSummary && (
          <p className="whitespace-pre-wrap leading-relaxed text-apolo-textSecondary">
            {summary}
          </p>
        )}

        {/* ── Passos: lista numerada (to-do) ── */}
        {safeSteps.length > 0 && (
          <ol className="flex flex-col gap-2" aria-label="Passos do plano">
            {safeSteps.map((s, i) => (
              <li
                key={i}
                className="group/step -mx-2 flex items-start gap-3 rounded-apolo-md px-2 py-1 transition-colors hover:bg-apolo-surface2/50"
              >
                <span
                  className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-apolo-accentBorder bg-apolo-accentMuted font-mono text-[10px] tabular-nums text-apolo-accent transition-transform group-hover/step:scale-110"
                  aria-hidden="true"
                >
                  {s.n}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-apolo-textPrimary">{s.action}</span>
                    {s.tool && (
                      <code className="rounded-apolo-sm border border-apolo-borderSubtle bg-apolo-surface2 px-1.5 py-0.5 font-mono text-[10.5px] text-apolo-textTertiary">
                        {s.tool}
                      </code>
                    )}
                  </div>
                  {s.success && (
                    <p className="mt-0.5 text-[11.5px] leading-snug text-apolo-textTertiary">
                      {s.success}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}

        {/* ── Perguntas de esclarecimento: chips clicáveis + "Outro" ── */}
        {safeQuestions.length > 0 && (
          <div className="flex flex-col gap-3.5 border-t border-apolo-borderSubtle pt-3.5">
            <span className="font-mono text-[10.5px] uppercase tracking-wider text-apolo-textTertiary" aria-hidden="true">
              Perguntas
            </span>
            {safeQuestions.map((q) => {
              const otherSelected = q.multi
                ? Array.isArray(answers[q.id]?.choice) && answers[q.id].choice.includes(OTHER_VALUE)
                : answers[q.id]?.choice === OTHER_VALUE;
              const inputType = q.multi ? 'checkbox' : 'radio';
              return (
                <fieldset key={q.id} className="min-w-0" data-testid={`plan-question-${q.id}`}>
                  <legend className="mb-2 flex items-center gap-1.5 text-sm font-medium text-apolo-textPrimary">
                    <MessageSquare size={13} className="shrink-0 text-apolo-accent" aria-hidden="true" />
                    {q.question}
                  </legend>
                  {/* Opções como LINHAS grandes selecionáveis (estilo Claude Code),
                   * com indicador radio/checkbox à esquerda — não chips pequenos. */}
                  <div className="flex flex-col gap-1.5">
                    {q.options.map((opt, oi) => {
                      const selected = isSelected(q.id, opt, q.multi);
                      return (
                        <label
                          key={oi}
                          className={[
                            'flex w-full cursor-pointer items-center gap-2.5 rounded-apolo-md border px-3 py-2.5 text-sm transition',
                            'min-h-touch focus-within:outline-none focus-within:ring-2 focus-within:ring-apolo-accentRing active:scale-[0.99]',
                            selected
                              ? 'border-apolo-accentBorder bg-apolo-accentMuted text-apolo-textPrimary'
                              : 'border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textSecondary hover:border-apolo-border hover:text-apolo-textPrimary',
                          ].join(' ')}
                        >
                          <input
                            type={inputType}
                            name={q.id}
                            value={opt}
                            checked={selected}
                            onChange={() =>
                              q.multi ? toggleMulti(q.id, opt) : pickSingle(q.id, opt)
                            }
                            className="sr-only"
                          />
                          <span
                            aria-hidden="true"
                            className={[
                              'flex h-[18px] w-[18px] shrink-0 items-center justify-center border transition-colors',
                              q.multi ? 'rounded-[5px]' : 'rounded-full',
                              selected ? 'border-apolo-accent bg-apolo-accent text-apolo-bg' : 'border-apolo-border',
                            ].join(' ')}
                          >
                            {/* (T4) Cursor-style numbered options — the index shows in the
                                indicator until picked, then the check takes over. aria-hidden
                                so the radio's accessible name stays the option text. */}
                            {selected ? (
                              <Check size={12} strokeWidth={3} aria-hidden="true" />
                            ) : (
                              <span className="font-mono text-[9.5px] leading-none text-apolo-textTertiary">{oi + 1}</span>
                            )}
                          </span>
                          <span className="min-w-0 flex-1">{opt}</span>
                        </label>
                      );
                    })}

                    {/* "Outro" — SEMPRE presente (a menos que allowOther:false) */}
                    {q.allowOther && (
                      <label
                        className={[
                          'flex w-full cursor-pointer items-center gap-2.5 rounded-apolo-md border px-3 py-2.5 text-sm transition',
                          'min-h-touch focus-within:outline-none focus-within:ring-2 focus-within:ring-apolo-accentRing active:scale-[0.99]',
                          otherSelected
                            ? 'border-apolo-accentBorder bg-apolo-accentMuted text-apolo-textPrimary'
                            : 'border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textSecondary hover:border-apolo-border hover:text-apolo-textPrimary',
                        ].join(' ')}
                      >
                        <input
                          type={inputType}
                          name={q.id}
                          value={OTHER_VALUE}
                          checked={otherSelected}
                          onChange={() =>
                            q.multi ? toggleMulti(q.id, OTHER_VALUE) : pickSingle(q.id, OTHER_VALUE)
                          }
                          className="sr-only"
                        />
                        <span
                          aria-hidden="true"
                          className={[
                            'flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border transition-colors',
                            otherSelected ? 'border-apolo-accent bg-apolo-accent text-apolo-bg' : 'border-apolo-border',
                          ].join(' ')}
                        >
                          {otherSelected && <Check size={12} strokeWidth={3} aria-hidden="true" />}
                        </span>
                        <Pencil size={13} className="shrink-0 text-apolo-textTertiary" aria-hidden="true" />
                        <span className="min-w-0 flex-1">Outro</span>
                      </label>
                    )}
                  </div>

                  {/* Campo de texto livre — revelado quando "Outro" está marcado */}
                  {q.allowOther && otherSelected && (
                    <div className="mt-2">
                      <label htmlFor={`plan-other-${q.id}`} className="sr-only">
                        Resposta personalizada para: {q.question}
                      </label>
                      <input
                        id={`plan-other-${q.id}`}
                        type="text"
                        value={answers[q.id]?.otherText || ''}
                        onChange={(e) => setOtherText(q.id, e.target.value)}
                        placeholder="Digite sua resposta…"
                        autoComplete="off"
                        className="w-full rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface2 px-3 py-1.5 text-[12.5px] text-apolo-textPrimary placeholder:text-apolo-textPlaceholder focus:border-apolo-borderFocus focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
                      />
                    </div>
                  )}
                </fieldset>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Footer: 1 primário (Aprovar) + 1 secundário (Rejeitar) ── */}
      <footer className="flex flex-col gap-2.5 border-t border-apolo-borderSubtle px-4 py-3">
        {rejecting && (
          <div>
            <label htmlFor="plan-reject-feedback" className="sr-only">
              Por que rejeitar o plano
            </label>
            <input
              id="plan-reject-feedback"
              type="text"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="O que mudar no plano? (opcional)"
              autoComplete="off"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  onReject?.(feedback.trim());
                }
              }}
              className="w-full rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface2 px-3 py-2 text-[12.5px] text-apolo-textPrimary placeholder:text-apolo-textPlaceholder focus:border-apolo-borderFocus focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing"
            />
          </div>
        )}
        {!rejecting && (
          <p className="text-[11px] leading-snug text-apolo-textTertiary">
            Revise os passos e responda às perguntas. Você pode pedir ajustes no chat antes de aprovar.
          </p>
        )}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleRejectClick}
            className="inline-flex min-h-touch items-center gap-1.5 rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface2 px-3.5 py-1.5 text-[12.5px] font-medium text-apolo-textSecondary transition hover:border-apolo-border hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing active:scale-[0.98]"
          >
            <X size={13} aria-hidden="true" />
            {rejecting ? 'Enviar feedback' : 'Rejeitar'}
          </button>
          {/* (T4) Cursor-style Skip — proceed WITHOUT answering the clarifying
              questions (approve with no answers; the agent uses its judgment).
              Only shown when there are questions and we're not in reject mode. */}
          {safeQuestions.length > 0 && !rejecting && (
            <button
              type="button"
              onClick={() => onApprove?.([])}
              data-testid="plan-skip-questions"
              className="inline-flex min-h-touch items-center rounded-apolo-md border border-apolo-borderSubtle bg-apolo-surface2 px-3.5 py-1.5 text-[12.5px] font-medium text-apolo-textSecondary transition hover:border-apolo-border hover:text-apolo-textPrimary focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing active:scale-[0.98]"
            >
              Pular
            </button>
          )}
          <button
            type="button"
            onClick={handleApprove}
            aria-label="Aprovar"
            title="Aprovar o plano e iniciar a execução"
            className="inline-flex min-h-touch items-center gap-1.5 rounded-apolo-md border border-transparent bg-apolo-accent px-4 py-1.5 text-[12.5px] font-medium text-apolo-bg shadow-sm transition hover:bg-apolo-accentHover focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accentRing focus-visible:ring-offset-1 focus-visible:ring-offset-apolo-surface1 active:scale-[0.98]"
          >
            <Send size={13} aria-hidden="true" />
            Aprovar e executar
          </button>
        </div>
      </footer>
    </section>
  );
}
